import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a pagniated results list
 */
const PLP_FRAME_FIX_STYLE = `
@media screen and (max-width: 749px) {
  .collection-wrapper .product-grid .product-grid__item,
  .collection-wrapper .product-grid .product-card__content,
  .collection-wrapper .product-grid .product-grid__card,
  .collection-wrapper .product-grid .card-gallery,
  .collection-wrapper .product-grid .border-style,
  .collection-wrapper .product-grid slideshow-slide,
  .collection-wrapper .product-grid .product-media-container {
    border: none !important;
    border-width: 0 !important;
    --border-width: 0 !important;
    outline: none !important;
    box-shadow: none !important;
  }
}`;

export default class ResultsList extends PaginatedList {
  #gridObserver;

  connectedCallback() {
    super.connectedCallback();
    this.#injectMobileFrameFix();
    this.#observeGridUpdates();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');
  }

  #observeGridUpdates() {
    const grid = this.querySelector('.product-grid');
    if (!grid) return;

    this.#gridObserver?.disconnect();
    this.#gridObserver = new MutationObserver(() => {
      this.#injectMobileFrameFix();
    });
    this.#gridObserver.observe(grid, { childList: true, subtree: true });
  }

  #injectMobileFrameFix() {
    if (!this.querySelector('.collection-wrapper .product-grid')) return;
    if (!window.matchMedia('(max-width: 749px)').matches) return;

    if (!document.getElementById('plp-frame-fix-style')) {
      const style = document.createElement('style');
      style.id = 'plp-frame-fix-style';
      style.textContent = PLP_FRAME_FIX_STYLE;
      document.head.appendChild(style);
    }

    this.querySelectorAll(
      '.collection-wrapper .product-grid .border-style, .collection-wrapper .product-grid .product-grid__card, .collection-wrapper .product-grid .card-gallery, .collection-wrapper .product-grid > .product-grid__item'
    ).forEach((el) => {
      el.style.setProperty('--border-width', '0px', 'important');
      el.style.setProperty('border-width', '0px', 'important');
      el.style.setProperty('border-style', 'none', 'important');
    });
  }

  disconnectedCallback() {
    this.#gridObserver?.disconnect();
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
